import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Select from 'react-select';
import {
    Search, RotateCcw, Eye, Printer, ChevronUp,
    Calendar, Package, DollarSign, TrendingUp, CheckCircle, AlertCircle,
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const ProductionHistory = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProduction, setSelectedProduction] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    const [filters, setFilters] = useState({
        search: '', status: '', from_date: '', to_date: '', batch_no: ''
    });

    const [pagination, setPagination] = useState({
        page: 1, limit: 10, total: 0, totalPages: 1
    });

    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    const canView = manufacturingActions.hasManufacturingPermission('view-production');

    // ── Load productions ──────────────────────────────────────────────────────
    // FIX: useCallback so we can call it from filter handlers without stale closures
    const loadProductions = useCallback(async (page = 1, currentFilters = filters) => {
        if (!canView) { toast.error('No permission to view productions'); return; }
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: currentFilters.search,
                status: currentFilters.status,
                from_date: currentFilters.from_date,
                to_date: currentFilters.to_date,
                batch_no: currentFilters.batch_no,
            };
            const result = await manufacturingActions.getProductions(params);
            if (result.success) {
                setProductions(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1,
                }));
            } else {
                toast.error(result.message || 'Failed to load productions');
            }
        } catch (err) {
            toast.error('Failed to load productions');
        } finally {
            setLoading(false);
        }
    }, [pagination.limit, canView]); // NOTE: filters NOT in deps — passed explicitly

    useEffect(() => { loadProductions(1, filters); }, []); // eslint-disable-line

    // ── Filter handlers (FIX: pass updated filter to avoid stale closure) ─────
    const handleFilterChange = (key, value) => {
        const updated = { ...filters, [key]: value };
        setFilters(updated);
        loadProductions(1, updated);
    };

    const clearFilters = () => {
        const empty = { search: '', status: '', from_date: '', to_date: '', batch_no: '' };
        setFilters(empty);
        loadProductions(1, empty);
    };

    // ── View production ───────────────────────────────────────────────────────
    const handleViewProduction = async (production) => {
        setViewLoading(true);
        setShowViewModal(true);
        try {
            const result = await manufacturingActions.getProduction(production.id);
            if (result.success) {
                setSelectedProduction(result.data);
            } else {
                toast.error(result.message || 'Failed to load production details');
                setShowViewModal(false);
            }
        } catch {
            toast.error('Failed to load production details');
            setShowViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

    const getStatusBadge = (status) => {
        const map = { completed: 'bg-success', draft: 'bg-warning', cancelled: 'bg-danger' };
        return <span className={`badge ${map[status] || 'bg-secondary'}`}>{(status || 'N/A').toUpperCase()}</span>;
    };

    const renderTooltip = (props, text) => <Tooltip id={`tt-${text}`} {...props}>{text}</Tooltip>;

    // FIX: use total_production_cost (not total_cost which doesn't exist in DB)
    const totals = {
        totalProductions: pagination.total,
        totalUnits: productions.reduce((s, p) => s + (parseFloat(p.total_quantity) || 0), 0),
        totalCost: productions.reduce((s, p) => s + (parseFloat(p.total_production_cost) || 0), 0),
        completed: productions.filter(p => p.status === 'completed').length,
    };

    const Pagination = () => {
        if (pagination.totalPages <= 1) return null;
        const { page, totalPages, total, limit } = pagination;
        const from = (page - 1) * limit + 1;
        const to = Math.min(page * limit, total);

        const pageNums = [];
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        for (let i = start; i <= end; i++) pageNums.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">Showing {from} to {to} of {total} entries</div>
                <nav><ul className="pagination">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadProductions(page - 1, filters)}>Previous</button>
                    </li>
                    {pageNums.map(n => (
                        <li key={n} className={`page-item ${page === n ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => loadProductions(n, filters)}>{n}</button>
                        </li>
                    ))}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadProductions(page + 1, filters)}>Next</button>
                    </li>
                </ul></nav>
            </div>
        );
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* ── Header ── */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Production History</h4>
                        <h6>View and track all manufacturing productions</h6>
                    </div>
                    <ul className="table-top-head">
                        {[['PDF', 'pdf'], ['Excel', 'excel']].map(([label, icon]) => (
                            <li key={label}>
                                <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, label)}>
                                    <Link to="#"><ImageWithBasePath src={`assets/img/icons/${icon}.svg`} alt={label} /></Link>
                                </OverlayTrigger>
                            </li>
                        ))}
                        <li><OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Print')}>
                            <Link onClick={() => window.print()}><Printer /></Link>
                        </OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Refresh')}>
                            <Link onClick={() => loadProductions(1, filters)}><RotateCcw /></Link>
                        </OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Collapse')}>
                            <Link className={data ? "active" : ""} onClick={() => dispatch(setToogleHeader(!data))}><ChevronUp /></Link>
                        </OverlayTrigger></li>
                    </ul>
                </div>

                {/* ── Stat cards ── */}
                <div className="row">
                    {[
                        { label: 'Total Productions', value: totals.totalProductions, icon: <Package size={24} className="text-primary" />, bg: 'bg-primary' },
                        { label: 'Completed', value: totals.completed, icon: <CheckCircle size={24} className="text-success" />, bg: 'bg-success' },
                        { label: 'Total Units Produced', value: totals.totalUnits.toLocaleString('en-IN'), icon: <TrendingUp size={24} className="text-info" />, bg: 'bg-info' },
                        { label: 'Total Production Cost', value: formatCurrency(totals.totalCost), icon: <DollarSign size={24} className="text-warning" />, bg: 'bg-warning' },
                    ].map(({ label, value, icon, bg }) => (
                        <div key={label} className="col-lg-3 col-md-6 col-sm-12">
                            <div className="card"><div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className={`${bg} bg-opacity-10 p-3 rounded me-3`}>{icon}</div>
                                    <div><p className="text-muted mb-1">{label}</p><h3 className="mb-0">{value}</h3></div>
                                </div>
                            </div></div>
                        </div>
                    ))}
                </div>

                {/* ── Filters ── */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3">
                                <label>Search</label>
                                <div className="input-group">
                                    <input type="text" className="form-control" placeholder="Production no, batch no..."
                                        value={filters.search}
                                        onChange={e => handleFilterChange('search', e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadProductions(1, filters)} />
                                    <button className="btn btn-primary" onClick={() => loadProductions(1, filters)}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <label>Batch No</label>
                                <input type="text" className="form-control" placeholder="Batch number"
                                    value={filters.batch_no}
                                    onChange={e => handleFilterChange('batch_no', e.target.value)} />
                            </div>
                            <div className="col-lg-2">
                                <label>Status</label>
                                <Select options={statusOptions} isClearable placeholder="All Status"
                                    value={statusOptions.find(o => o.value === filters.status) || null}
                                    onChange={sel => handleFilterChange('status', sel?.value || '')} />
                            </div>
                            <div className="col-lg-2">
                                <label>From Date</label>
                                <input type="date" className="form-control" value={filters.from_date}
                                    onChange={e => handleFilterChange('from_date', e.target.value)} />
                            </div>
                            <div className="col-lg-2">
                                <label>To Date</label>
                                <input type="date" className="form-control" value={filters.to_date}
                                    onChange={e => handleFilterChange('to_date', e.target.value)} />
                            </div>
                            <div className="col-lg-1">
                                <label>&nbsp;</label>
                                <button className="btn btn-secondary w-100" onClick={clearFilters}>Clear</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Table ── */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                            </div>
                        ) : productions.length === 0 ? (
                            <div className="text-center py-5">
                                <Package size={48} className="text-muted mb-3" />
                                <h5>No productions found</h5>
                                <p className="text-muted">No production records match your filters</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead><tr>
                                        <th>Production No</th><th>Batch No</th><th>Date</th>
                                        <th>Raw Materials</th><th>Finished Goods</th>
                                        <th>Total Qty</th><th>Total Cost</th>
                                        <th>Status</th><th>Converted</th>
                                        <th className="text-center">Actions</th>
                                    </tr></thead>
                                    <tbody>
                                        {productions.map(prod => (
                                            <tr key={prod.id}>
                                                <td><strong>{prod.production_no}</strong></td>
                                                <td><code>{prod.batch_no}</code></td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Calendar size={14} className="me-1 text-muted" />
                                                        {formatDate(prod.production_date)}
                                                    </div>
                                                </td>
                                                <td>{prod.raw_materials_count || 0} items</td>
                                                <td>{prod.finished_goods_count || 0} items</td>
                                                <td>{parseFloat(prod.total_quantity || 0).toLocaleString('en-IN')}</td>
                                                {/* FIX: use total_production_cost */}
                                                <td className="fw-bold">{formatCurrency(prod.total_production_cost)}</td>
                                                <td>{getStatusBadge(prod.status)}</td>
                                                <td>
                                                    {prod.converted_to_purchase
                                                        ? <span className="badge bg-success d-inline-flex align-items-center gap-1"><CheckCircle size={11} /> Converted</span>
                                                        : <span className="badge bg-secondary">Pending</span>}
                                                </td>
                                                <td className="text-center">
                                                    <button className="btn btn-sm btn-outline-info"
                                                        onClick={() => handleViewProduction(prod)} title="View Details">
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <Pagination />
                    </div>
                </div>
            </div>

            {/* ── View Modal (FULLY IMPLEMENTED — was placeholder before) ── */}
            {showViewModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Package size={18} className="me-2" />
                                    Production Details{selectedProduction ? ` — ${selectedProduction.production_no}` : ''}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)} />
                            </div>
                            <div className="modal-body">
                                {viewLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status" />
                                    </div>
                                ) : selectedProduction ? (
                                    <>
                                        {/* Info cards */}
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body">
                                                        <h6 className="mb-3">Production Information</h6>
                                                        <p><strong>Production No:</strong> {selectedProduction.production_no}</p>
                                                        <p><strong>Batch No:</strong> <code>{selectedProduction.batch_no}</code></p>
                                                        <p><strong>Date:</strong> {formatDate(selectedProduction.production_date)}</p>
                                                        <p><strong>Status:</strong> {getStatusBadge(selectedProduction.status)}</p>
                                                        {selectedProduction.notes && <p><strong>Notes:</strong> {selectedProduction.notes}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body">
                                                        <h6 className="mb-3">Cost Summary</h6>
                                                        <p><strong>Raw Materials Cost:</strong> {formatCurrency(selectedProduction.total_raw_material_cost)}</p>
                                                        <p><strong>Production Cost:</strong> {formatCurrency(selectedProduction.total_production_cost)}</p>
                                                        <p><strong>Wastage Cost:</strong> <span className="text-danger">{formatCurrency(selectedProduction.total_wastage_cost)}</span></p>
                                                        <p><strong>Margin:</strong>
                                                            <span className={parseFloat(selectedProduction.total_production_cost) >= parseFloat(selectedProduction.total_raw_material_cost) ? 'text-success ms-1' : 'text-danger ms-1'}>
                                                                {formatCurrency(selectedProduction.total_production_cost - selectedProduction.total_raw_material_cost)}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Raw Materials */}
                                        {selectedProduction.raw_materials?.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fw-bold mb-3">Raw Materials Used</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <thead className="table-light"><tr>
                                                            <th>Product</th><th>SKU</th>
                                                            <th>Qty Used</th><th>Unit</th>
                                                            <th>Unit Cost</th><th>Total Cost</th>
                                                            <th>Stock Before</th><th>Stock After</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {selectedProduction.raw_materials.map((item, i) => (
                                                                <tr key={i}>
                                                                    <td>{item.product_name}</td>
                                                                    <td><code>{item.sku}</code></td>
                                                                    <td>{parseFloat(item.quantity_used).toLocaleString('en-IN')}</td>
                                                                    <td>{item.unit}</td>
                                                                    <td>{formatCurrency(item.unit_cost)}</td>
                                                                    <td className="fw-bold">{formatCurrency(item.total_cost)}</td>
                                                                    <td className="text-muted">{item.stock_before}</td>
                                                                    <td className="text-muted">{item.stock_after}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-light"><tr>
                                                            <td colSpan="5" className="text-end fw-bold">Total Raw Material Cost:</td>
                                                            <td className="fw-bold">{formatCurrency(selectedProduction.total_raw_material_cost)}</td>
                                                            <td colSpan="2" />
                                                        </tr></tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Finished Goods */}
                                        {selectedProduction.finished_goods?.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fw-bold mb-3">Finished Goods Produced</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <thead className="table-light"><tr>
                                                            <th>Product</th><th>SKU</th>
                                                            <th>Qty Produced</th><th>Unit</th>
                                                            <th>Unit Cost</th><th>Selling Price</th>
                                                            <th>Total Cost</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {selectedProduction.finished_goods.map((item, i) => (
                                                                <tr key={i}>
                                                                    <td>{item.product_name}</td>
                                                                    <td><code>{item.sku}</code></td>
                                                                    <td>{parseFloat(item.quantity_produced).toLocaleString('en-IN')}</td>
                                                                    <td>{item.unit}</td>
                                                                    <td>{formatCurrency(item.unit_cost)}</td>
                                                                    <td>{formatCurrency(item.selling_price)}</td>
                                                                    <td className="fw-bold text-success">{formatCurrency(item.total_cost)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-light"><tr>
                                                            <td colSpan="6" className="text-end fw-bold">Total Production Cost:</td>
                                                            <td className="fw-bold">{formatCurrency(selectedProduction.total_production_cost)}</td>
                                                        </tr></tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Wastage */}
                                        {selectedProduction.wastage?.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fw-bold mb-3">Wastage Recorded</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <thead className="table-light"><tr>
                                                            <th>Product</th><th>Qty</th><th>Unit</th>
                                                            <th>Type</th><th>Reason</th><th>Cost</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {selectedProduction.wastage.map((w, i) => (
                                                                <tr key={i}>
                                                                    <td>{w.product_name}</td>
                                                                    <td className="text-danger fw-bold">{w.wastage_quantity}</td>
                                                                    <td>{w.unit}</td>
                                                                    <td><span className="badge bg-warning">{w.wastage_type}</span></td>
                                                                    <td>{w.reason}</td>
                                                                    <td className="text-danger">{formatCurrency(w.wastage_cost)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-light"><tr>
                                                            <td colSpan="5" className="text-end fw-bold">Total Wastage Cost:</td>
                                                            <td className="fw-bold text-danger">{formatCurrency(selectedProduction.total_wastage_cost)}</td>
                                                        </tr></tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Conversion status */}
                                        {selectedProduction.converted_to_purchase ? (
                                            <div className="alert alert-success">
                                                <CheckCircle size={16} className="me-2" />
                                                Converted to purchase order: <strong>{selectedProduction.purchase_invoice_no}</strong>
                                            </div>
                                        ) : selectedProduction.status === 'completed' && (
                                            <div className="alert alert-info">
                                                <AlertCircle size={16} className="me-2" />
                                                This production is ready to convert to a purchase order.
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-4 text-muted">Failed to load details</div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionHistory;
